import React, { useEffect } from "react";
import { X, Printer, Download, Mail } from "lucide-react";
import { formatBRDate } from "../utils/date";

const currency = (v) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v || 0);

const fmtDate = (iso) => {
  if (!iso) return "—";
  // Formato longo (dd de mês de aaaa) sem sofrer com timezone
  const s = String(iso).trim();
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) {
    const meses = ["janeiro","fevereiro","março","abril","maio","junho","julho","agosto","setembro","outubro","novembro","dezembro"];
    return `${parseInt(m[3], 10)} de ${meses[parseInt(m[2], 10) - 1]} de ${m[1]}`;
  }
  return formatBRDate(iso);
};

/**
 * Recibo em A5 (paisagem) pronto para impressão / PDF via print dialog.
 * Aparência de talão de recibo tradicional com brasão, número sequencial, valor
 * por extenso e assinatura do responsável.
 */
export const ReceiptPrintModal = ({ isOpen, onClose, receipt, onSendEmail }) => {
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [isOpen, onClose]);

  if (!isOpen || !receipt) return null;

  const snap = receipt.config_snapshot || {};
  const isReceita = receipt.type === "receita";

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-black/85 backdrop-blur-md print:bg-white print:p-0"
      data-testid="receipt-print-modal"
    >
      <div className="w-full max-w-3xl print:max-w-none flex flex-col print:block">
        {/* Toolbar (hidden on print) */}
        <div className="flex items-center justify-between mb-3 print:hidden">
          <h3 className="text-sm font-serif font-bold text-white uppercase tracking-widest">
            {receipt.number_label}
          </h3>
          <div className="flex items-center gap-2">
            {onSendEmail && (
              <button
                data-testid="receipt-btn-send-email"
                onClick={() => onSendEmail(receipt)}
                className="flex items-center space-x-2 bg-[#131C2E] hover:bg-[#1E293B] text-white border border-[#D4AF37]/40 font-semibold px-4 py-2 rounded-xl text-xs uppercase tracking-wider transition-all"
              >
                <Mail className="w-3.5 h-3.5 text-[#D4AF37]" />
                <span>Enviar por e-mail</span>
              </button>
            )}
            <button
              data-testid="receipt-btn-print"
              onClick={() => window.print()}
              className="flex items-center space-x-2 bg-[#D4AF37] hover:bg-[#C5A028] text-slate-950 font-bold px-4 py-2 rounded-xl text-xs uppercase tracking-wider transition-all"
            >
              <Printer className="w-3.5 h-3.5" />
              <span>Imprimir / Salvar PDF</span>
            </button>
            <button
              data-testid="receipt-btn-close"
              onClick={onClose}
              className="p-2 rounded-xl bg-slate-800 text-slate-300 hover:text-white"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Receipt paper */}
        <div
          id="receipt-paper"
          className="bg-white text-slate-900 rounded-2xl shadow-[0_0_40px_rgba(212,175,55,0.15)] p-10 print:shadow-none print:rounded-none print:p-8 print:m-0"
          style={{ fontFamily: "'Georgia', 'Times New Roman', serif" }}
        >
          {/* Header */}
          <div className="flex items-start justify-between border-b-2 border-slate-800 pb-4 mb-6">
            <div className="flex items-start gap-4">
              {snap.lodge_logo_b64 && (
                <img
                  src={snap.lodge_logo_b64}
                  alt="Brasão"
                  className="w-20 h-20 object-contain"
                />
              )}
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500">
                  À Glória do Grande Arquiteto do Universo
                </div>
                <h2 className="text-lg font-bold uppercase leading-tight">
                  {snap.lodge_name || "Loja Maçônica"}
                  {snap.lodge_number ? ` Nº ${snap.lodge_number}` : ""}
                </h2>
                <div className="text-xs text-slate-600 mt-0.5">
                  {snap.lodge_oriente ? `Oriente de ${snap.lodge_oriente}` : ""}
                  {snap.lodge_obediencia ? ` • ${snap.lodge_obediencia}` : ""}
                </div>
                {snap.cnpj && (
                  <div className="text-xs text-slate-600 mt-0.5">
                    CNPJ: <span className="font-mono">{snap.cnpj}</span>
                  </div>
                )}
                {snap.address && (
                  <div className="text-xs text-slate-600">{snap.address}</div>
                )}
              </div>
            </div>

            <div className="text-right border-2 border-slate-800 rounded-lg px-4 py-2">
              <div className="text-[9px] uppercase tracking-widest text-slate-500">Recibo</div>
              <div className="text-2xl font-bold font-mono tabular-nums">
                Nº {String(receipt.number).padStart(4, "0")}
              </div>
              <div className="text-[10px] uppercase mt-1 text-slate-600">
                {isReceita ? "Entrada" : "Saída"}
              </div>
            </div>
          </div>

          {/* Value */}
          <div className="flex items-center justify-between bg-slate-50 border border-slate-300 rounded-lg px-5 py-3 mb-5">
            <div className="text-xs uppercase tracking-widest text-slate-500">Valor</div>
            <div className="text-3xl font-bold font-mono text-slate-900">
              {currency(receipt.amount)}
            </div>
          </div>

          {/* Body */}
          <div className="space-y-3 text-sm leading-relaxed">
            <p>
              Recebi(emos) de <strong>{receipt.payer_name}</strong>, a importância
              de <strong>{currency(receipt.amount)}</strong>
              {receipt.amount_extenso && (
                <>
                  {" "}(<em>{receipt.amount_extenso}</em>)
                </>
              )}
              , correspondente a <strong>{receipt.description || "—"}</strong>
              {receipt.category && (
                <>
                  {" "}— Categoria: <strong>{receipt.category}</strong>
                  {receipt.subcategory ? ` / ${receipt.subcategory}` : ""}
                </>
              )}
              {receipt.payment_method && (
                <>
                  , recebido via <strong>{receipt.payment_method}</strong>
                </>
              )}
              .
            </p>
            <p>
              E, para clareza firmo o presente recibo em{" "}
              <strong>
                {snap.lodge_oriente ? `${snap.lodge_oriente}, ` : ""}
                {fmtDate(receipt.payment_date)}
              </strong>
              .
            </p>
          </div>

          {/* Signature */}
          <div className="mt-14 flex flex-col items-center text-center">
            {snap.signature_b64 && (
              <img
                src={snap.signature_b64}
                alt="Assinatura"
                className="max-h-20 mb-1"
                style={{ maxWidth: 280 }}
              />
            )}
            <div className="border-t-2 border-slate-800 w-80 pt-1 text-xs">
              <div className="font-bold uppercase">
                {snap.responsible_name || "—"}
              </div>
              <div className="text-slate-600">
                {snap.responsible_role || "Tesoureiro"}
                {snap.lodge_name ? ` — ${snap.lodge_name}` : ""}
              </div>
            </div>
          </div>

          {/* Footer */}
          {snap.footer_text && (
            <div className="mt-8 pt-3 border-t border-slate-300 text-[10px] text-slate-500 text-center italic">
              {snap.footer_text}
            </div>
          )}
        </div>
      </div>

      <style>{`
        @media print {
          @page {
            size: A5 landscape;
            margin: 12mm;
          }
          body * {
            visibility: hidden;
          }
          #receipt-paper, #receipt-paper * {
            visibility: visible;
          }
          #receipt-paper {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
          }
        }
      `}</style>
    </div>
  );
};
